import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { PaymentRailsApi } from '@/api/clients/payment-rails/payment-rails.api';
import {
  useGetPaymentRailCatalog,
  getPaymentRailDisplayName,
} from '@/api/hooks/payment-rails/useGetPaymentRailCatalog';
import { useGetOrganizationPaymentRailSetting } from '@/api/hooks/payment-rails/useGetOrganizationPaymentRailSetting';
import { useConfirmation } from '@/context/ConfirmationContext';
import PaymentRailFormDialog from './PaymentRailFormDialog';

export default function PaymentRailsSection({
  organizationId,
  paymentRailSettingId,
}) {
  const { t } = useTranslation();
  const { catalog } = useGetPaymentRailCatalog(organizationId);
  const {
    settings: orgPaymentRailSettings,
    loading: orgSettingsLoading,
  } = useGetOrganizationPaymentRailSetting(organizationId);
  const { requestConfirmation } = useConfirmation();
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRail, setEditingRail] = useState(null);

  const existingKeys = useMemo(
    () =>
      new Set(
        rails.map((r) =>
          r.subType == null ? `${r.method}` : `${r.method}-${r.subType}`,
        ),
      ),
    [rails],
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await PaymentRailsApi.list(
        organizationId,
        paymentRailSettingId,
      );
      setRails(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId && paymentRailSettingId) refresh();
  }, [organizationId, paymentRailSettingId]);

  const handleAdd = () => {
    setEditingRail(null);
    setDialogOpen(true);
  };

  const handleEdit = (rail) => {
    setEditingRail(rail);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRail(null);
  };

  const handleToggleEnabled = async (rail) => {
    setBusyId(rail.id);
    try {
      await PaymentRailsApi.update(organizationId, rail.id, {
        is_enabled: !rail.isEnabled,
      });
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = (rail) => {
    const name = getPaymentRailDisplayName(catalog, rail.method, rail.subType);
    requestConfirmation(t('paymentRails.confirmDelete', { name }), async () => {
      setBusyId(rail.id);
      try {
        await PaymentRailsApi.delete(organizationId, rail.id);
        await refresh();
      } finally {
        setBusyId(null);
      }
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          {t('paymentRails.sectionTitle')}
        </Typography>
        <Button
          size='small'
          variant='outlined'
          startIcon={<Add />}
          onClick={handleAdd}>
          {t('paymentRails.addButton')}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : rails.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
          {t('paymentRails.empty')}
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{t('paymentRails.table.rail')}</TableCell>
              <TableCell>{t('paymentRails.table.workflows')}</TableCell>
              <TableCell>{t('paymentRails.table.entityTypes')}</TableCell>
              <TableCell>{t('paymentRails.table.enabled')}</TableCell>
              <TableCell align='right'>
                {t('paymentRails.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rails.map((rail) => (
              <TableRow key={rail.id}>
                <TableCell>
                  {getPaymentRailDisplayName(
                    catalog,
                    rail.method,
                    rail.subType,
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {rail.workflows.map((w) => {
                      const allowed = !!orgPaymentRailSettings[w]?.has(
                        rail.method,
                      );
                      const chip = (
                        <Chip
                          key={w}
                          label={t(`paymentRails.workflows.${w}`, {
                            defaultValue: w,
                          })}
                          size='small'
                          color={
                            orgSettingsLoading
                              ? 'default'
                              : allowed
                              ? 'success'
                              : 'error'
                          }
                          variant={
                            orgSettingsLoading || allowed
                              ? 'filled'
                              : 'outlined'
                          }
                        />
                      );
                      if (orgSettingsLoading) return chip;
                      return (
                        <Tooltip
                          key={w}
                          title={t(
                            allowed
                              ? 'paymentRails.workflows.allowedTooltip'
                              : 'paymentRails.workflows.notAllowedTooltip',
                          )}>
                          {chip}
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell>
                  {rail.entityTypes && rail.entityTypes.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {rail.entityTypes.map((et) => (
                        <Chip
                          key={et}
                          label={t(`paymentRails.entityTypes.${et}`, {
                            defaultValue: et,
                          })}
                          size='small'
                        />
                      ))}
                    </Box>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Switch
                    size='small'
                    checked={rail.isEnabled}
                    disabled={busyId === rail.id}
                    onChange={() => handleToggleEnabled(rail)}
                  />
                </TableCell>
                <TableCell align='right'>
                  <Tooltip title={t('paymentRails.editTooltip')}>
                    <span>
                      <IconButton
                        size='small'
                        color='primary'
                        disabled={busyId === rail.id}
                        onClick={() => handleEdit(rail)}>
                        <Edit fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('paymentRails.deleteTooltip')}>
                    <span>
                      <IconButton
                        size='small'
                        color='error'
                        disabled={busyId === rail.id}
                        onClick={() => handleDelete(rail)}>
                        <Delete fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PaymentRailFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        organizationId={organizationId}
        paymentRailSettingId={paymentRailSettingId}
        rail={editingRail}
        existingKeys={existingKeys}
        onSaved={refresh}
      />
    </Box>
  );
}
